/**
 * TS-side helpers that compose the Rust core's `redactSecrets`. The core
 * handles single-string redaction; recursive / JSON-aware shapes belong
 * up here so the FFI surface stays minimal.
 */
import { native } from "./native.js";

/**
 * Canonical secret kinds reported by the Rust core in `SecretMatch.kind`.
 * Wire is permissive (modelled as a free string in {@link SecretMatch})
 * so unknown kinds don't break callers; use this union when narrowing.
 */
export type SecretKind =
  | "anthropic"
  | "openai"
  | "openai_project"
  | "github"
  | "google"
  | "google_oauth"
  | "aws_access_key"
  | "aws_secret_key"
  | "stripe"
  | "slack"
  | "slack_webhook"
  | "sendgrid"
  | "twilio_sid"
  | "mailgun"
  | "npm_token"
  | "jwt"
  | "private_key_pem"
  | "context_secret"
  | "bearer"
  | "base64"
  | "generic_token";

/**
 * Walk a JSON-shaped value and redact secrets inside every string leaf.
 * Object keys are not touched; only values. Arrays and nested objects
 * are recursed structurally so the returned value has the same shape.
 */
export function redactJsonStrings<T>(value: T): T {
  if (typeof value === "string") {
    return native.redactSecrets(value).redacted as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => redactJsonStrings(v)) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = redactJsonStrings(v);
    }
    return out as unknown as T;
  }
  return value;
}
